// Weekly invoicing (Admin → Invoices).
//
// Both reads are `realtime` + `volatile`: an invoice moves because a
// batch-worker action ran, not because a domain revision advanced, and the
// `/data-revisions` manifest carries no billing domain on purpose (the plan
// weighed a tenth domain against polling a page an admin opens monthly and
// chose polling). Mutations name these resources through `volatileResources`.

import { api } from '@/api/client';
import { CADENCE, FRESHNESS, defineQuery } from '@/api/resource';
import { buildQs } from '@/api/query-string';
import type { InvoiceDetailResponse, InvoiceListResponse, InvoiceState } from '@/api/types';
import { hasOpenAction } from '@/lib/invoicing-view';

/**
 * What `GET /admin/invoices` reads. Wire names: `buildQs` enumerates the
 * object and the server parses `period_from` / `period_to` / `state`.
 * `state` is OMITTED (never `undefined`) for "all", so the loader and the
 * screen hash the same key — `invoicesListQuery` is the only thing that
 * builds one.
 */
export interface InvoicesQuery {
  period_from?: string;
  period_to?: string;
  state?: InvoiceState;
}

function clean(q: InvoicesQuery): Record<string, string> {
  const out: Record<string, string> = {};
  if (q.period_from) out.period_from = q.period_from;
  if (q.period_to) out.period_to = q.period_to;
  if (q.state) out.state = q.state;
  return out;
}

/**
 * The list, with the recent action feed and the config slice the screen
 * explains itself with. No `unwrap`: `settings` and `actions` are not
 * envelope, they are what the screen renders around the rows.
 *
 * `FRESHNESS.seconds` rather than the namespace's `staleTime: 0`: the route
 * loader blocks on this read, and a slot that is stale the instant `warm`
 * fills it fetches twice on every entry (`adminRemediations` learned this).
 * `CADENCE.slow` keeps an open list honest against a batch executor that
 * runs within a minute of a click.
 */
export const adminInvoices = defineQuery({
  namespace: 'realtime',
  name: 'admin.invoices',
  volatile: true,
  keyOf: (q: InvoicesQuery) => [clean(q)],
  freshness: FRESHNESS.seconds,
  cadence: CADENCE.slow,
  refetchOnWindowFocus: true,
  request: (q: InvoicesQuery, signal) =>
    api.get<InvoiceListResponse>(`/admin/invoices${buildQs(clean(q))}`, { signal }),
});

/**
 * One invoice with its lines, send attempts and actions.
 *
 * Polls every two seconds while an action is queued or running — the drawer
 * is watching a ladder walk — and drops to the list's tick otherwise.
 * `retry: false`: a bad `?invoice=` is a stable 404, not something to grind
 * through the realtime backoff.
 */
export const adminInvoiceById = defineQuery({
  namespace: 'realtime',
  name: 'admin.invoiceById',
  volatile: true,
  keyOf: (id: string) => [id],
  freshness: FRESHNESS.intent,
  cadence: (raw: InvoiceDetailResponse | undefined) =>
    raw && hasOpenAction(raw.actions) ? CADENCE.urgent : CADENCE.slow,
  refetchOnWindowFocus: true,
  retry: false,
  request: (id: string, signal) =>
    api.get<InvoiceDetailResponse>(`/admin/invoices/${encodeURIComponent(id)}`, { signal }),
});
